import {
  LinkButtonTo,
  Multiselect,
  SelectItem,
  Stack,
  Switch,
  Title,
  useModals,
} from "@skailer-com/uikit";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  useJobEditData,
  useJobEditForm,
} from "@/pages/recruit/jobs/edit/components/job-edit.type.ts";
import { JobEditCareerSiteFieldsModal } from "@/pages/recruit/jobs/edit/widgets/JobEditCareerSiteFieldsModal.tsx";
import { useCareerSiteGetAll } from "@/queries/recruit/use-career-sites.query.ts";
import { ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

export function JobEditCareer() {
  const { control, watch, getValues, setValue } = useJobEditForm();
  const { openModal } = useModals();
  const { sections } = useJobEditData();
  const isPublicTitleVisible = sections.some((section) =>
    section.fields.some((field) => field.type === ApiFieldTypes.job_public_title)
  );

  const { t } = useTranslation();
  const { isFeatureEnabled } = usePermissions();
  const { data: sites } = useCareerSiteGetAll(isFeatureEnabled(ApiFeatureType.ats_career_site));

  const publishOnCareerSite = watch("publish_on_career_site");

  const careerSitesOptions = useMemo(() => {
    if (!sites) return [];

    return sites
      .filter((site) => !site.is_archived)
      .map((site) => {
        return { id: site.id, title: site.title };
      }) as SelectItem[];
  }, [sites]);

  if (!isFeatureEnabled(ApiFeatureType.ats_career_site)) {
    return null;
  }

  const handleOpenSetupFields = () => {
    openModal(JobEditCareerSiteFieldsModal, {
      config: getValues("career_site_apply_config"),
      publicTitle: getValues("public_title"),
      onChange: (config) => {
        setValue("career_site_apply_config", config, { shouldDirty: true });
      },
      onPublicTitleChange: isPublicTitleVisible
        ? (publicTitle) => setValue("public_title", publicTitle)
        : undefined,
    });
  };

  return (
    <Stack className={"card-panel"} gap={"2xl"}>
      <Title header={t("career_sites:career_page")} size="md" />
      <Stack gap={"md"}>
        <Controller
          control={control}
          name={"publish_on_career_site"}
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value}
              onChange={onChange}
              title={t("career_sites:publish_on_career_page")}
              description={t("career_sites:activate_to_publish")}
            />
          )}
        />
        {publishOnCareerSite && (
          <>
            <Controller
              control={control}
              name={"career_site_ids"}
              render={({ field: { value, onChange } }) => (
                <Multiselect
                  onChange={onChange}
                  label={t("career_sites:choose_page")}
                  emptyTitle={t("common:select")}
                  values={value ?? []}
                  options={careerSitesOptions}
                />
              )}
            />
            <LinkButtonTo onClick={handleOpenSetupFields}>{t("ats:setup_job_page")}</LinkButtonTo>
          </>
        )}
      </Stack>
    </Stack>
  );
}
